import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FilterLayout } from "../layout/FilterLayout";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
} from "@mui/material";
import Button from "../ui/Button";
import useAuthUser from "../../hooks/useAuthUser";
import { GRIEVANCE_STATUS_OPTIONS } from "../../constants/grievanceStatus";
import { getUsersByRole } from "../../services/grievance";

// pms_roles.id for HR Manager / HR Executive — the people a grievance can be
// assigned to (same hardcoded role-id convention as GrievanceList.jsx).
const HR_MANAGER_ROLE_ID = 2;
const HR_EXECUTIVE_ROLE_ID = 3;

// The dashboard's Open / Overdue / Reopened counts, as one list filter.
const viewOptions = [
  { label: "Open", value: "open" },
  { label: "Overdue", value: "overdue" },
  { label: "Reopened", value: "reopened" },
];

const emptyValues = {
  reference: "",
  from_date: "",
  to_date: "",
  assigned_to_me: false,
  assigned_to: "",
  category_id: "",
  priority: "",
  status: "",
  department: "",
  view: "",
};

// List params -> form values, so a dashboard drill-down shows up in the
// filter bar (and can be changed or cleared there).
const paramsToValues = (params = {}) => ({
  ...emptyValues,
  ...(params.reference ? { reference: params.reference } : {}),
  ...(params.from_date ? { from_date: params.from_date } : {}),
  ...(params.to_date ? { to_date: params.to_date } : {}),
  ...(params.category_id ? { category_id: params.category_id } : {}),
  ...(params.priority ? { priority: params.priority } : {}),
  ...(params.status !== undefined ? { status: String(params.status) } : {}),
  ...(params.department ? { department: params.department } : {}),
  ...(params.assigned_to ? { assigned_to: params.assigned_to } : {}),
  view: params.overdue
    ? "overdue"
    : params.reopened
      ? "reopened"
      : params.open
        ? "open"
        : "",
});

const priorityOptions = [
  { label: "Low", value: "Low" },
  { label: "Medium", value: "Medium" },
  { label: "High", value: "High" },
];

const departmentOptions = [
  { label: "HR", value: "HR" },
  { label: "Admin", value: "Admin" },
  { label: "Support", value: "Support" },
  { label: "Account", value: "Account" },
  { label: "IT", value: "IT" },
];

// seed: { params, nonce } — set by the page when the dashboard opens the list
// with a filter; a new nonce re-seeds the form even if the params look the same.
const GrievanceFilter = ({ onFilter, exportGrievances, categories = [], seed }) => {
  const { canHandleGrievance, canManageGrievance } = useAuthUser();
  const { control, handleSubmit, reset, getValues } = useForm({
    defaultValues: paramsToValues(seed?.params),
  });
  const [assignees, setAssignees] = useState([]);

  useEffect(() => {
    if (seed) reset(paramsToValues(seed.params));
    // eslint-disable-next-line
  }, [seed?.nonce]);

  // "Assigned To" lists the HR Manager(s) and HR Executives who can own a case.
  useEffect(() => {
    if (!canManageGrievance) return;
    Promise.all([
      getUsersByRole(HR_MANAGER_ROLE_ID),
      getUsersByRole(HR_EXECUTIVE_ROLE_ID),
    ])
      .then(([managers, executives]) => {
        const all = [...(managers.data?.data || []), ...(executives.data?.data || [])];
        setAssignees(all.filter((u, i) => all.findIndex((x) => x.id === u.id) === i));
      })
      .catch((error) => console.log("Error loading assignees:", error));
  }, [canManageGrievance]);

  const buildParams = (data) => {
    const searchParams = {};
    if (data?.reference?.trim()) searchParams.reference = data.reference.trim();
    if (data?.category_id) searchParams.category_id = data.category_id;
    if (data?.priority) searchParams.priority = data.priority;
    if (data?.status !== "" && data?.status !== undefined)
      searchParams.status = data.status;
    if (data?.department) searchParams.department = data.department;
    if (data?.from_date) searchParams.from_date = data.from_date;
    if (data?.to_date) searchParams.to_date = data.to_date;
    if (data?.assigned_to_me) searchParams.assigned_to_me = true;
    if (data?.assigned_to) searchParams.assigned_to = data.assigned_to;
    if (data?.view) searchParams[data.view] = true;
    return searchParams;
  };

  const onSubmit = (data) => {
    onFilter(buildParams(data));
  };

  const handleClear = () => {
    reset(emptyValues);
    onFilter({});
  };

  const handleExport = () => {
    exportGrievances(buildParams(getValues()));
  };

  return (
    <FilterLayout>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="reference"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reference No"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    backgroundColor: "white",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                />
              )}
            />
          </div>

          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="category_id"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "white", marginTop: "8px", marginBottom: "8px" }}
                >
                  <Autocomplete
                    {...field}
                    options={categories}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.name || ""
                    }
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    value={categories.find((c) => c.id === field.value) || null}
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.id : "");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Category" size="small" />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "white", marginTop: "8px", marginBottom: "8px" }}
                >
                  <Autocomplete
                    {...field}
                    options={priorityOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    value={priorityOptions.find((o) => o.value === field.value) || null}
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Priority" size="small" />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "white", marginTop: "8px", marginBottom: "8px" }}
                >
                  <Autocomplete
                    {...field}
                    options={GRIEVANCE_STATUS_OPTIONS}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    value={
                      GRIEVANCE_STATUS_OPTIONS.find((o) => o.value === field.value) || null
                    }
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Status" size="small" />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="department"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "white", marginTop: "8px", marginBottom: "8px" }}
                >
                  <Autocomplete
                    {...field}
                    options={departmentOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    value={departmentOptions.find((o) => o.value === field.value) || null}
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Department" size="small" />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          {canManageGrievance && (
            <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
              <Controller
                name="view"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ backgroundColor: "white", marginTop: "8px", marginBottom: "8px" }}
                  >
                    <Autocomplete
                      {...field}
                      options={viewOptions}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.label
                      }
                      isOptionEqualToValue={(option, value) => option.value === value?.value}
                      value={viewOptions.find((o) => o.value === field.value) || null}
                      onChange={(event, newValue) => {
                        field.onChange(newValue ? newValue.value : "");
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Select View" size="small" />
                      )}
                    />
                  </FormControl>
                )}
              />
            </div>
          )}

          {canManageGrievance && (
            <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
              <Controller
                name="assigned_to"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ backgroundColor: "white", marginTop: "8px", marginBottom: "8px" }}
                  >
                    <Autocomplete
                      {...field}
                      options={assignees}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.name || ""
                      }
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      value={assignees.find((u) => u.id === field.value) || null}
                      onChange={(event, newValue) => {
                        field.onChange(newValue ? newValue.id : "");
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Assigned To (HR)" size="small" />
                      )}
                    />
                  </FormControl>
                )}
              />
            </div>
          )}

          {["from_date", "to_date"].map((name) => (
            <div key={name} className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
              <Controller
                name={name}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    label={name === "from_date" ? "Raised From" : "Raised To"}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      backgroundColor: "white",
                      marginTop: "8px",
                      marginBottom: "8px",
                    }}
                  />
                )}
              />
            </div>
          ))}

          {canHandleGrievance && (
            <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
              <Controller
                name="assigned_to_me"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Assigned to me"
                    sx={{ marginTop: "8px" }}
                  />
                )}
              />
            </div>
          )}

          <div className="col-lg-3 col-md-4 col-sm-12 col-xs-12 mt-2">
            <Button type="submit" variant="success">
              Filter
            </Button>
            <Button
              type="button"
              variant="dark"
              onClick={handleClear}
              style={{ marginLeft: "5px" }}
            >
              Clear
            </Button>
            <Button
              type="button"
              variant="dark"
              style={{ marginLeft: "5px" }}
              onClick={handleExport}
            >
              Export
            </Button>
          </div>
        </div>
      </form>
    </FilterLayout>
  );
};

export default GrievanceFilter;
